import { IdentityProvider, useLogger } from "@vp/ubik-context";
import { ErrorBoundary } from "react-error-boundary";
import type { Props as AccessoriesProps } from "../../fragments/accessories";
import type { Props as ProductsProps } from "../../fragments/recommendations";
import { AlbelliContextProvider } from "../albelli/context";
import { AuthContextConsumer, AuthContextProvider } from "../auth/context";
import { CartContextProvider } from "../cart/context";
import { ContentSourcesContextProvider } from "../content/context";
import { toLoggableError } from "../error/error";
import { LogoUrlContextProvider } from "../logo/context";
import { ProductContextProvider } from "../product/context";
import { WorkContextProvider } from "../work/context";

type RecommendationsShellProps = {
  render: () => React.ReactNode;
} & (ProductsProps | AccessoriesProps);

export function RecommendationsShell(props: RecommendationsShellProps) {
  const logger = useLogger();
  const { auth, render, ...otherProps } = props;

  return (
    <ErrorBoundary
      fallback={<></>}
      onError={(error: Error) => {
        logger.error(
          "Error captured at recommendations' error boundary",
          toLoggableError(error),
          {
            props: otherProps,
          },
        );
      }}
    >
      <IdentityProvider auth={auth}>
        <AuthContextProvider>
          <AuthContextConsumer>
            {(authContext) => {
              if (!authContext) {
                return <></>;
              }
              return (
                <WorkContextProvider workId={props.workId}>
                  <AlbelliContextProvider documentId={props.albelliDocId}>
                    <LogoUrlContextProvider
                      logoUrl={props.logoUrl}
                      logoId={props.logoId}
                    >
                      <CartContextProvider
                        disabled={
                          !props.workId && !props.albelliDocId && !props.logoId
                        }
                        locale={props.locale}
                      >
                        <ProductContextProvider>
                          <ContentSourcesContextProvider
                            brandId={(props as ProductsProps).brandId}
                          >
                            {render()}
                          </ContentSourcesContextProvider>
                        </ProductContextProvider>
                      </CartContextProvider>
                    </LogoUrlContextProvider>
                  </AlbelliContextProvider>
                </WorkContextProvider>
              );
            }}
          </AuthContextConsumer>
        </AuthContextProvider>
      </IdentityProvider>
    </ErrorBoundary>
  );
}
